/**
 * P2-A · make a manually-created client ATTRIBUTABLE.
 *
 * THE DEFECT THIS FIXES
 *
 * `citation_to_commission_funnel_v1` can only see a client that satisfies BOTH:
 *
 *   1. a row in `lead_acquisition_snapshots` — whose ONLY writer is the
 *      `AFTER INSERT ON leads` trigger `trg_snapshot_lead_acquisition`
 *      (migration 051). No lead insert, no snapshot, ever.
 *   2. `clients.origin_lead_id` — whose ONLY writer is the
 *      `claim_or_convert_lead` RPC on convert (migration 057).
 *
 * Manual client creation used to INSERT straight into `clients`, so it produced
 * neither. A manually-added client was therefore invisible to revenue
 * attribution **permanently, by construction** — not lagging, never arriving.
 * Website and SMS leads already satisfy both and were fine. This closes the one
 * door that could not fix itself by waiting, which matters exactly as much as
 * walk-ins and referrals matter to the business.
 *
 * WHY THE RPC AND NOT A DIRECT `origin_lead_id` WRITE
 *
 * Writing `origin_lead_id` ourselves alongside the client insert would be two
 * writes instead of three and would avoid a network dependency. It is still
 * wrong, for two reasons:
 *
 *   - A documented invariant forbids it. `ApartmentDashboard.tsx` retired its own
 *     direct client INSERT specifically so "no dashboard path can bypass
 *     claim/conversion". Absence of a writer is not absence of a rule.
 *   - It would leave the lead at `status = 'new'` with no `lead_claims` row, so
 *     every manually-added client would ALSO spawn a phantom unclaimed lead in
 *     the New Leads feed. The RPC sets `status='converted'`, creates the claim,
 *     and attributes the client to its arrival record. Follow-up tasks remain
 *     operator-created so conversion does not silently add a second queue.
 *
 * KNOWN COST, STATED PLAINLY: `runLeadAction` goes through `locatenow-api`
 * (`fetchWithAuth`), so manual client creation now depends on that API being up,
 * where before it only needed Supabase. That is a real reduction in robustness
 * for this path and it is accepted deliberately — and it is NOT the same trade
 * the public intake path refuses. Losing a public lead loses a renter who never
 * comes back; a failed manual entry is in front of an operator who sees the
 * error and retries, and (see below) the arrival record survives regardless.
 */

export interface ManualIntakeLeadRow {
  name: string;
  email: string;
  phone?: string | null;
  source: string;
  status: "new";
}

export interface ManualIntakeDeps {
  /** Minimal shape of the supabase client this function needs. */
  insertLead: (row: ManualIntakeLeadRow) => Promise<{ id: string }>;
  /** The atomic claim/convert contract. Must be the real RPC path. */
  convertLead: (leadId: string) => Promise<{ client_id: string | null }>;
  /** Applies the operator's full form payload to the converted client. */
  updateClient: (clientId: string, payload: Record<string, unknown>) => Promise<void>;
}

/**
 * The lead was created but conversion did not complete.
 *
 * DISPOSITION — decided, not left open. We do NOT delete the lead:
 *
 *   - It is structurally impossible to delete where migration 051 is applied.
 *     `lead_acquisition_snapshots.lead_id` is
 *     `REFERENCES leads(id) ON DELETE RESTRICT`, and the snapshot row is written
 *     by trigger during the very insert we would be rolling back. The delete
 *     would be refused by the database.
 *   - It would also be the wrong thing even if it worked. A walk-in who gave us
 *     a name and an email IS a real lead. Deleting the arrival record to tidy up
 *     a failed second step destroys true information to hide a transient error.
 *
 * So the disposition is RETRY-FORWARD, not cleanup: the lead is left in place,
 * where it appears in the New Leads feed as an unclaimed lead, and the operator
 * finishes the conversion from there in one click. Nothing is lost and there is
 * an existing UI path to completion.
 *
 * This error exists so that outcome is SURFACED rather than swallowed. A generic
 * "failed to save client" would leave the operator believing nothing happened
 * while a real lead sat in their feed.
 */
export class ManualIntakeOrphanError extends Error {
  constructor(
    public readonly leadId: string,
    public readonly cause: unknown
  ) {
    super(
      "The client was not created, but the lead was saved and is in your New Leads " +
        "feed — convert it from there to finish. Nothing was lost."
    );
    this.name = "ManualIntakeOrphanError";
  }
}

/** Raised when convert succeeds but returns no client id — never assume one. */
export class ManualIntakeNoClientError extends Error {
  constructor(public readonly leadId: string) {
    super(
      "The lead was saved but conversion returned no client. It is in your New " +
        "Leads feed — convert it from there to finish."
    );
    this.name = "ManualIntakeNoClientError";
  }
}

/**
 * Build the arrival record for a manually-entered client.
 *
 * `source` is the operator's own `lead_source` when they supplied one, and
 * otherwise the literal `"manual"`. `"manual"` is NOT a fabricated value — that
 * this client was entered by hand is a fact we know to be true, which is the
 * line the no-fabrication invariants draw (`NULL must mean unknown, never a
 * manufactured value`). We never guess a CHANNEL here; naming the door is not
 * naming the campaign.
 */
export function buildManualLeadRow(
  payload: Record<string, unknown>
): ManualIntakeLeadRow {
  const first = String(payload.first_name ?? "").trim();
  const last = String(payload.last_name ?? "").trim();
  const email = String(payload.email ?? "").trim();
  const rawSource = payload.lead_source;
  const source =
    typeof rawSource === "string" && rawSource.trim() ? rawSource.trim() : "manual";
  const rawPhone = payload.phone;
  const phone =
    typeof rawPhone === "string" && rawPhone.trim() ? rawPhone.trim() : null;

  return {
    name: [first, last].filter(Boolean).join(" "),
    email,
    phone,
    source,
    status: "new",
  };
}

/**
 * Create a manually-entered client so that revenue attribution can see it.
 *
 * Order is load-bearing: lead first (so the snapshot trigger fires), then
 * convert (so `origin_lead_id` is set by its only legitimate writer), then apply
 * the operator's form values — because the RPC creates the client with its own
 * defaults (`status='new_client'`, `client_type='renter'`, `priority='normal'`)
 * and would otherwise silently discard whatever the operator actually chose.
 *
 * A failure in the final update is NOT an orphan: the client exists and is
 * editable, so it is allowed to propagate as an ordinary save error.
 */
export async function createAttributableClient(
  deps: ManualIntakeDeps,
  payload: Record<string, unknown>
): Promise<{ clientId: string; leadId: string }> {
  const lead = await deps.insertLead(buildManualLeadRow(payload));

  let clientId: string | null;
  try {
    const result = await deps.convertLead(lead.id);
    clientId = result.client_id;
  } catch (error) {
    throw new ManualIntakeOrphanError(lead.id, error);
  }
  if (!clientId) {
    throw new ManualIntakeNoClientError(lead.id);
  }

  await deps.updateClient(clientId, payload);
  return { clientId, leadId: lead.id };
}
